import { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_CODES } from '../common/constants/languageCodes';
import { applyHandoffLanguage, isSupportedLanguage, rememberHandoffLanguage } from '../utils/handoffLanguage';

export const LanguageProvider = ({ children, isWithLanguageParam = true }) => {
    const [searchParams] = useSearchParams();
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    // the lang this provider put in the URL itself is the language already shown, not one the visitor came with
    const addedLangRef = useRef(null);

    useEffect(() => {
        const langParam = searchParams.get('lang');

        if (!isWithLanguageParam) {
            if (!langParam) {
                return;
            }

            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('lang');

            navigate(
                {
                    pathname: location.pathname,
                    search: newSearchParams.toString(),
                },
                { replace: true }
            );

            return;
        }

        if (!langParam) {
            const currentLang = i18n.language || navigator.language.slice(0, 2);
            const validLang = isSupportedLanguage(currentLang) ? currentLang : LANGUAGE_CODES.ENGLISH;

            // keep the rest of the query: the 2FA step after Google sign-in gets only the landing hand-off
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('lang', validLang);
            addedLangRef.current = validLang;

            navigate(
                {
                    pathname: location.pathname,
                    search: newSearchParams.toString(),
                },
                { replace: true }
            );

            return;
        }

        if (addedLangRef.current === langParam) {
            return;
        }

        // SC-QA-36: the language the visitor came with holds for this visit, after sign-in too, but is not saved
        if (isSupportedLanguage(langParam)) {
            rememberHandoffLanguage(langParam);
            if (i18n.language !== langParam) applyHandoffLanguage(i18n, langParam);
        }
    }, [searchParams, i18n, navigate, location, isWithLanguageParam]);

    return children;
};
